/**
 * ConciliacionBancaria.js — Conciliación Bancaria persistente (por cuenta y corte)
 *
 * Flujo (como lo trabaja un contador):
 *  1. Elegir cuenta bancaria y fecha de corte (ej. fin de mes).
 *  2. El sistema muestra el saldo inicial (de la última conciliación CERRADA)
 *     y los movimientos PENDIENTES de conciliar (incluye arrastrados de meses
 *     anteriores: cheques sin cambiar, transferencias no acreditadas, etc.).
 *  3. Ingresar el saldo final del estado de cuenta del banco.
 *  4. Marcar ✓ los movimientos que SÍ aparecen en el estado de cuenta
 *     (a mano o importando el Excel/CSV del banco).
 *  5. "Guardar borrador" persiste los checks — se puede salir a investigar
 *     diferencias y volver después.
 *  6. Cuando saldo inicial + movimientos marcados == saldo del banco,
 *     se habilita "Cerrar conciliación". Los no marcados quedan pendientes
 *     y aparecen automáticamente en la conciliación siguiente.
 */
import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import apiFetch from "../../../../shared/services/apiClient";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";
import styles from "../styles/conciliacion.module.css";

const BASE_URL = process.env.REACT_APP_API_URL;

const r2 = (n) => Math.round(Number(n) * 100) / 100;

function fmtDate(iso) {
  if (!iso || iso.length < 10) return iso ?? "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

/** Hoy en hora local (no UTC) */
function todayLocal() {
  const d = new Date(Date.now() - new Date().getTimezoneOffset() * 60000);
  return d.toISOString().split("T")[0];
}

function getAuthHeaders() {
  const rol = localStorage.getItem("rol");
  const isSuper = localStorage.getItem("is_superuser") === "true";
  return {
    Authorization: `Bearer ${localStorage.getItem("access_token")}`,
    "Content-Type": "application/json",
    ...((rol === "CONTADOR" || isSuper)
      ? { "X-Condominio-ID": localStorage.getItem("active_condominio_id") || "" }
      : {}),
  };
}

/** Extrae montos numéricos de una celda del archivo del banco */
function parseMonto(v) {
  if (v == null || v === "") return 0;
  if (typeof v === "number") return v;
  let s = String(v).replace(/[^\d.,-]/g, "");
  if (!s) return 0;
  const lastComma = s.lastIndexOf(",");
  const lastDot   = s.lastIndexOf(".");
  if (lastComma > -1 && lastDot > -1) {
    if (lastComma > lastDot) s = s.replace(/\./g, "").replace(",", ".");
    else s = s.replace(/,/g, "");
  } else if (lastComma > -1) {
    s = s.length - lastComma - 1 === 2 ? s.replace(",", ".") : s.replace(/,/g, "");
  }
  return Number(s) || 0;
}

export default function ConciliacionBancaria() {
  const { t } = useTranslation();
  const fileRef = useRef(null);

  const [cuentas,     setCuentas]     = useState([]);
  const [cuentaId,    setCuentaId]    = useState("");
  const [fechaCorte,  setFechaCorte]  = useState(todayLocal());
  const [saldoBanco,  setSaldoBanco]  = useState("");
  const [data,        setData]        = useState(null);
  const [movs,        setMovs]        = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [closing,     setClosing]     = useState(false);
  const [exporting,   setExporting]   = useState(null);
  const [msg,         setMsg]         = useState(null);
  const [matchInfo,   setMatchInfo]   = useState(null);
  const [historial,   setHistorial]   = useState([]);
  const [confirmDel,  setConfirmDel]  = useState(null);  // conciliación a eliminar
  const [deleting,    setDeleting]    = useState(false);

  const cuenta   = cuentas.find((c) => String(c.id) === String(cuentaId));
  const simbolo  = cuenta?.moneda === "USD" ? "$" : "₡";
  const cerrada  = !!data?.cerrada;

  // ── Cargar cuentas bancarias ────────────────────────────────────────────────
  useEffect(() => {
    apiFetch("/api/finance/cuentaBancaria/?all=1")
      .then((r) => r.json())
      .then((d) => {
        const results = Array.isArray(d) ? d : d.results ?? [];
        setCuentas(results);
        if (results.length === 1) setCuentaId(String(results[0].id));
      })
      .catch(console.error);
  }, []);

  function showMsg(ok, text) {
    setMsg({ ok, text });
    setTimeout(() => setMsg(null), 6000);
  }

  async function fetchHistorial(cid) {
    try {
      const res = await apiFetch(`/api/finance/conciliacion/historial/?cuenta_bancaria=${cid}`);
      if (res.ok) setHistorial(await res.json());
    } catch { /* noop */ }
  }

  // ── Cargar datos de conciliación ────────────────────────────────────────────
  async function cargar() {
    if (!cuentaId) { showMsg(false, t("conciliacion.seleccionaCuenta")); return; }
    setLoading(true);
    setMatchInfo(null);
    try {
      const qs = new URLSearchParams({ cuenta_bancaria: cuentaId, fecha_corte: fechaCorte });
      const res = await apiFetch(`/api/finance/conciliacion/data/?${qs}`);
      if (!res.ok) throw new Error(t("conciliacion.errorCargar"));
      const d = await res.json();
      setData(d);
      setMovs(d.movimientos);
      const saldoGuardado = d.cerrada?.saldo_banco ?? d.borrador?.saldo_banco;
      setSaldoBanco(saldoGuardado != null && saldoGuardado !== 0 ? String(saldoGuardado) : "");
      fetchHistorial(cuentaId);
    } catch (e) {
      showMsg(false, e.message);
    } finally {
      setLoading(false);
    }
  }

  function toggle(id) {
    if (cerrada) return;
    setMovs((prev) => prev.map((m) => (m.id === id ? { ...m, conciliado: !m.conciliado } : m)));
  }
  function toggleAll(value) {
    if (cerrada) return;
    setMovs((prev) => prev.map((m) => ({ ...m, conciliado: value })));
  }

  // ── Importar archivo del banco ──────────────────────────────────────────────
  async function importarArchivo(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    try {
      const buf = await file.arrayBuffer();
      const wb  = XLSX.read(buf, { type: "array" });
      const ws  = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: "" });

      const montosBanco = new Map();
      const textoBanco  = [];
      for (const row of rows) {
        for (const cell of row) {
          const n = Math.abs(parseMonto(cell));
          if (n > 0.009) montosBanco.set(r2(n), (montosBanco.get(r2(n)) || 0) + 1);
          if (typeof cell === "string" && cell.trim()) textoBanco.push(cell.toLowerCase());
        }
      }
      const textoTodo = textoBanco.join(" | ");

      let matched = 0;
      setMovs((prev) =>
        prev.map((m) => {
          if (m.conciliado) return m;
          const monto = r2(Math.abs(Number(m.credito) > 0 ? m.credito : m.debito));
          const doc = (m.documento || "").trim().toLowerCase();
          const docMatch = doc.length >= 4 && textoTodo.includes(doc);
          const montoMatch = monto > 0 && (montosBanco.get(monto) || 0) > 0;
          if (docMatch || montoMatch) {
            if (montoMatch) montosBanco.set(monto, montosBanco.get(monto) - 1);
            matched += 1;
            return { ...m, conciliado: true };
          }
          return m;
        }),
      );
      setMatchInfo({ archivo: file.name, matched });
      showMsg(true, t("conciliacion.archivoProcesado", { archivo: file.name, count: matched }));
    } catch {
      showMsg(false, t("conciliacion.errorArchivo"));
    }
  }

  // ── Cálculos (lógica del contador) ──────────────────────────────────────────
  const marcados      = movs.filter((m) => m.conciliado);
  const pendientes    = movs.filter((m) => !m.conciliado);
  const netoMarcados  = r2(marcados.reduce((a, m) => a + (Number(m.credito) || 0) - (Number(m.debito) || 0), 0));
  const depTransito   = r2(pendientes.reduce((a, m) => a + (Number(m.credito) || 0), 0));
  const retTransito   = r2(pendientes.reduce((a, m) => a + (Number(m.debito) || 0), 0));
  const nDep          = pendientes.filter((m) => Number(m.credito) > 0).length;
  const nRet          = pendientes.filter((m) => Number(m.debito) > 0).length;

  const saldoInicial   = r2(data?.saldo_inicial_banco ?? 0);
  const saldoLibros    = r2(data?.saldo_libros ?? 0);
  const saldoBancoN    = r2(Number(String(saldoBanco).replace(",", ".")) || 0);
  // Lo que DEBERÍA tener el banco según lo conciliado:
  const saldoCalculado = r2(saldoInicial + netoMarcados);
  const diferencia     = r2(saldoCalculado - saldoBancoN);
  const cuadra         = Math.abs(diferencia) < 0.01;

  // ── Guardar borrador ────────────────────────────────────────────────────────
  async function guardarBorrador(silencioso = false) {
    if (!data) return null;
    setSaving(true);
    try {
      const res = await apiFetch("/api/finance/conciliacion/guardar/", {
        method: "POST",
        body: JSON.stringify({
          cuenta_bancaria: Number(cuentaId),
          fecha_corte: fechaCorte,
          saldo_banco: saldoBancoN,
          movimientos: marcados.map((m) => m.id),
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || t("conciliacion.errorGuardar"));
      if (!silencioso) showMsg(true, d.detail || t("conciliacion.borradorGuardado"));
      return d.id;
    } catch (e) {
      showMsg(false, e.message);
      return null;
    } finally {
      setSaving(false);
    }
  }

  // ── Cerrar conciliación ─────────────────────────────────────────────────────
  async function cerrarConciliacion() {
    const id = await guardarBorrador(true);
    if (!id) return;
    setClosing(true);
    try {
      const res = await apiFetch("/api/finance/conciliacion/cerrar/", {
        method: "POST",
        body: JSON.stringify({ id }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || t("conciliacion.errorCerrar"));
      showMsg(true, d.detail || t("conciliacion.conciliacionCerrada"));
      cargar();
    } catch (e) {
      showMsg(false, e.message);
    } finally {
      setClosing(false);
    }
  }

  // ── Eliminar/reabrir conciliación ───────────────────────────────────────────
  async function eliminarConciliacion() {
    if (!confirmDel) return;
    setDeleting(true);
    try {
      const res = await apiFetch("/api/finance/conciliacion/historial/", {
        method: "DELETE",
        body: JSON.stringify({ id: confirmDel.id }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || t("conciliacion.errorEliminar"));
      showMsg(true, d.detail);
      setConfirmDel(null);
      cargar();
    } catch (e) {
      showMsg(false, e.message);
    } finally {
      setDeleting(false);
    }
  }

  // ── Exportar PDF / Excel ────────────────────────────────────────────────────
  async function exportar(tipo) {
    if (!data) return;
    setExporting(tipo);
    try {
      const body = {
        cuenta_bancaria: Number(cuentaId),
        fecha_desde: data.ultima_conciliacion?.fecha_corte ?? null,
        fecha_hasta: fechaCorte,
        saldo_banco: saldoBancoN,
        saldo_libros: saldoLibros,
        movimientos: movs.map((m) => ({
          fecha: m.fecha, documento: m.documento, descripcion: m.descripcion,
          debito: m.debito, credito: m.credito, conciliado: m.conciliado,
        })),
      };
      const url = tipo === "pdf"
        ? "/api/finance/reportes/conciliacion-pdf/"
        : "/api/finance/reportes/conciliacion-excel/";
      const res = await fetch(`${BASE_URL}${url}`, {
        method: "POST", headers: getAuthHeaders(), body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(t("conciliacion.errorReporte"));
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = tipo === "pdf" ? "conciliacion_bancaria.pdf" : "conciliacion_bancaria.xlsx";
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) {
      showMsg(false, e.message);
    } finally {
      setExporting(null);
    }
  }

  const SummaryRow = ({ label, value, bold, valueColor, title }) => (
    <div className={styles["summary-row"]} title={title || label}>
      <span className={bold ? styles["summary-label-bold"] : styles["summary-label"]}>{label}</span>
      <span className={bold ? styles["summary-value-bold"] : styles["summary-value"]}
        style={valueColor ? { color: valueColor } : {}}>
        {simbolo} {formatCurrency(value)}
      </span>
    </div>
  );

  return (
    <>
      {/* Breadcrumb */}
      <span className={styles.inactive}>
        <Link to="/"><span className={styles.inactive}>{t("common.inicio")} /</span></Link>
        <span className={styles.active}> {t("conciliacion.titulo")}</span>
      </span>

      <h2 className="mt-2 me-2 p-0 title">{t("conciliacion.titulo")}</h2>
      <p className="mb-3" style={{ color: "var(--text-muted)" }}>
        {t("conciliacion.subtitulo")}
      </p>

      {/* Toast */}
      {msg && (
        <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 2000,
          background: msg.ok ? "rgba(74,222,128,0.07)" : "rgba(239,68,68,0.07)",
          border: `1px solid ${msg.ok ? "rgba(74,222,128,0.3)" : "rgba(239,68,68,0.3)"}`,
          borderLeft: `4px solid ${msg.ok ? "#4ade80" : "#f87171"}`,
          borderRadius: 8, padding: "12px 18px", display: "flex", alignItems: "center",
          gap: 10, boxShadow: "0 8px 32px rgba(0,0,0,0.4)", maxWidth: 460,
          backdropFilter: "blur(8px)" }}>
          <i className={`fa-solid ${msg.ok ? "fa-circle-check" : "fa-circle-exclamation"}`}
             style={{ color: msg.ok ? "#4ade80" : "#f87171", fontSize: "1.1rem" }}/>
          <span style={{ color: msg.ok ? "#4ade80" : "#f87171", fontWeight: 600 }}>{msg.text}</span>
        </div>
      )}

      {/* ── Cuenta y corte ── */}
      <div className={styles.card} style={{ marginBottom: 16 }}>
        <div className={styles["card-header"]}>
          <h1>
            <i className={`fa-solid fa-building-columns ${styles["icon-orange"]}`}/>
            {t("conciliacion.cuentaCorte")}
          </h1>
          {data?.ultima_conciliacion ? (
            <span style={{ fontSize: 13, color: "var(--text-muted)" }}>
              {t("conciliacion.ultimaConciliacion")} <b style={{ color: "var(--text-primary)" }}>
                {fmtDate(data.ultima_conciliacion.fecha_corte)}</b> — {t("conciliacion.saldoBancoLabel")}{" "}
              <b style={{ color: "#4ade80" }}>{simbolo} {formatCurrency(data.ultima_conciliacion.saldo_banco)}</b>
            </span>
          ) : data ? (
            <span style={{ fontSize: 13, color: "var(--text-muted)" }}>
              {t("conciliacion.sinPrevias", { simbolo })}
            </span>
          ) : null}
        </div>
        <div className={styles["card-body"]}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 14, alignItems: "flex-end" }}>
            <div className={styles.field} style={{ minWidth: 260, flex: "1 1 260px", maxWidth: 380 }}>
              <label>{t("conciliacion.cuentaBancaria")} <span style={{ color: "#f97316" }}>*</span></label>
              <select value={cuentaId} onChange={(e) => setCuentaId(e.target.value)}>
                <option value="">{t("conciliacion.seleccionar")}</option>
                {cuentas.map((c) => (
                  <option key={c.id} value={c.id}>{c.banco} — {c.numero_cuenta} ({c.moneda})</option>
                ))}
              </select>
            </div>
            <div className={styles.field}>
              <label>{t("conciliacion.fechaCorte")} <span style={{ color: "#f97316" }}>*</span></label>
              <input type="date" value={fechaCorte} onChange={(e) => setFechaCorte(e.target.value)}/>
            </div>
            <button onClick={cargar} disabled={loading}
              className={`${styles.btn} ${styles["btn-primary"]}`}>
              <i className={`fa-solid ${loading ? "fa-spinner fa-spin" : "fa-magnifying-glass"}`}/>
              {loading ? t("conciliacion.cargando") : t("conciliacion.procesar")}
            </button>
          </div>
        </div>
      </div>

      {data && (
        <div className={styles.grid}>

          {/* ── Movimientos pendientes ── */}
          <div className={styles.card}>
            <div className={styles["card-header"]}>
              <h1>
                <i className={`fa-solid fa-list-check ${styles["icon-blue"]}`}/>
                {t("conciliacion.movsPorConciliar")}
                <span style={{ fontWeight: 400, color: "var(--text-muted)", fontSize: 14 }}>
                  ({movs.length})
                </span>
                {marcados.length > 0 && (
                  <span className={styles["badge-match"]}>
                    <i className="fa-solid fa-check" style={{ marginRight: 4 }}/>
                    {marcados.length} {t("conciliacion.marcados")}
                  </span>
                )}
                {cerrada && (
                  <span className={styles["badge-match"]} style={{ borderColor: "rgba(96,165,250,0.4)", color: "#60a5fa", background: "rgba(96,165,250,0.1)" }}>
                    <i className="fa-solid fa-lock" style={{ marginRight: 4 }}/> {t("conciliacion.cerrada")}
                  </span>
                )}
              </h1>
              {!cerrada && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button onClick={() => fileRef.current?.click()}
                    className={`${styles.btn} ${styles["btn-import"]}`}
                    title={t("conciliacion.importarTitle")}>
                    <i className="fa-solid fa-file-import"/> {t("conciliacion.importarArchivo")}
                  </button>
                  <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv"
                    style={{ display: "none" }} onChange={importarArchivo}/>
                  <button onClick={() => toggleAll(true)}  className={`${styles.btn} ${styles["btn-outline"]}`}>
                    <i className="fa-solid fa-check-double"/> {t("conciliacion.todos")}
                  </button>
                  <button onClick={() => toggleAll(false)} className={`${styles.btn} ${styles["btn-outline"]}`}>
                    <i className="fa-solid fa-eraser"/> {t("conciliacion.ninguno")}
                  </button>
                </div>
              )}
            </div>

            <div className={styles["table-wrap"]}>
              <table className={styles["mov-table"]}>
                <thead>
                  <tr>
                    <th style={{ width: 42, textAlign: "center" }} title={t("conciliacion.checkTitle")}>
                      <i className="fa-solid fa-check" style={{ color: "#4ade80" }}/>
                    </th>
                    <th>{t("conciliacion.colFecha")}</th>
                    <th>{t("conciliacion.colDocumento")}</th>
                    <th>{t("conciliacion.colDescripcion")}</th>
                    <th style={{ textAlign: "right" }}>{t("conciliacion.colDebito")} {simbolo}</th>
                    <th style={{ textAlign: "right" }}>{t("conciliacion.colCredito")} {simbolo}</th>
                  </tr>
                </thead>
                <tbody>
                  {movs.length === 0 && (
                    <tr style={{ cursor: "default" }}>
                      <td colSpan={6}>
                        <div className={styles["empty-state"]}>
                          <i className="fa-solid fa-inbox"/>
                          {t("conciliacion.sinMovs")}
                        </div>
                      </td>
                    </tr>
                  )}
                  {movs.map((m) => (
                    <tr key={m.id} onClick={() => toggle(m.id)}
                      className={m.conciliado ? styles["row-conciliado"] : ""}
                      style={cerrada ? { cursor: "default" } : {}}>
                      <td style={{ textAlign: "center" }}>
                        <input type="checkbox" checked={m.conciliado} disabled={cerrada}
                          onChange={() => toggle(m.id)} onClick={(e) => e.stopPropagation()}/>
                      </td>
                      <td>{fmtDate(m.fecha)}</td>
                      <td>{m.documento || "—"}</td>
                      <td style={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis",
                        whiteSpace: "nowrap" }} title={m.descripcion}>
                        {m.descripcion || "—"}
                      </td>
                      <td style={{ textAlign: "right", fontFamily: "Inter, sans-serif",
                        color: m.debito > 0 ? "#f87171" : "var(--text-muted)",
                        fontWeight: m.debito > 0 ? 600 : 400 }}>
                        {m.debito > 0 ? formatCurrency(m.debito) : "—"}
                      </td>
                      <td style={{ textAlign: "right", fontFamily: "Inter, sans-serif",
                        color: m.credito > 0 ? "#4ade80" : "var(--text-muted)",
                        fontWeight: m.credito > 0 ? 600 : 400 }}>
                        {m.credito > 0 ? formatCurrency(m.credito) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Historial */}
            {historial.length > 0 && (
              <div style={{ borderTop: "1px solid var(--border-card)", padding: "12px 16px" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)",
                  textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>
                  {t("conciliacion.historial")}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {historial.map((h) => (
                    <div key={h.id} style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "center", fontSize: 13, gap: 8, flexWrap: "wrap" }}>
                      <span style={{ color: "var(--text-primary)" }}>
                        <i className={`fa-solid ${h.estado === "CERRADA" ? "fa-lock" : "fa-pen"}`}
                          style={{ marginRight: 6, color: h.estado === "CERRADA" ? "#4ade80" : "#fbbf24", fontSize: 11 }}/>
                        {fmtDate(h.fecha_corte)} — {simbolo} {formatCurrency(h.saldo_banco)}
                        <span style={{ color: "var(--text-muted)", marginLeft: 6 }}>
                          ({h.n_movimientos} {t("conciliacion.movAbrev")} · {h.estado === "CERRADA" ? t("conciliacion.estadoCerrada") : t("conciliacion.estadoBorrador")})
                        </span>
                      </span>
                      <button onClick={() => setConfirmDel(h)}
                        title={h.estado === "CERRADA" ? t("conciliacion.eliminarReabre") : t("conciliacion.eliminarBorrador")}
                        style={{ background: "none", border: "none", color: "#f87171",
                          cursor: "pointer", fontSize: 12 }}>
                        <i className="fa-solid fa-trash"/>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ── Panel de resumen ── */}
          <div className={styles.card} style={{ position: "sticky", top: 16 }}>
            <div className={styles["card-header"]}>
              <h1>
                <i className={`fa-solid fa-scale-balanced ${styles["icon-green"]}`}/>
                {t("conciliacion.panelTitulo")}
              </h1>
            </div>
            <div className={styles["card-body"]}>

              <div className={styles.field} style={{ marginBottom: 14 }}>
                <label title={t("conciliacion.saldoBancoTitle")}>
                  {t("conciliacion.saldoBancoAl", { fecha: fmtDate(fechaCorte) })} <span style={{ color: "#f97316" }}>*</span>
                </label>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontWeight: 700, color: "#137fec", fontSize: 15 }}>{simbolo}</span>
                  <input type="text" value={saldoBanco} placeholder="0.00" disabled={cerrada}
                    onChange={(e) => setSaldoBanco(e.target.value.replace(/[^0-9.,-]/g, ""))}
                    style={{ flex: 1, fontWeight: 600, textAlign: "right" }}/>
                </div>
              </div>

              {/* Validación del banco: inicial + marcados vs saldo banco */}
              <div className={styles["summary-box"]}>
                <SummaryRow
                  label={data.ultima_conciliacion
                    ? t("conciliacion.saldoInicialAl", { fecha: fmtDate(data.ultima_conciliacion.fecha_corte) })
                    : t("conciliacion.saldoInicial")}
                  title={data.ultima_conciliacion
                    ? t("conciliacion.saldoInicialTitleConPrev", { fecha: fmtDate(data.ultima_conciliacion.fecha_corte) })
                    : t("conciliacion.saldoInicialTitleSinPrev")}
                  value={saldoInicial} bold/>
                <SummaryRow label={t("conciliacion.conciliados", { count: marcados.length })}
                  title={t("conciliacion.conciliadosTitle")}
                  value={netoMarcados}/>
                <SummaryRow label={t("conciliacion.saldoCalculado")} value={saldoCalculado} bold
                  title={t("conciliacion.saldoCalculadoTitle")}/>
                <SummaryRow label={t("conciliacion.saldoSegunBanco")} value={saldoBancoN} bold
                  title={t("conciliacion.saldoSegunBancoTitle")}/>
              </div>

              <div className={cuadra ? styles["status-ok"] : styles["status-bad"]}>
                <span>
                  <i className={`fa-solid ${cuadra ? "fa-circle-check" : "fa-triangle-exclamation"}`}
                     style={{ marginRight: 7 }}/>
                  {cuadra ? t("conciliacion.concilia") : t("conciliacion.noCuadra")}
                </span>
                <span style={{ fontFamily: "Inter, sans-serif" }}>
                  {simbolo} {formatCurrency(diferencia)}
                </span>
              </div>

              {/* Pendientes (explican diferencia contra libros) */}
              <div className={styles["summary-box"]} style={{ marginTop: 10 }}>
                <SummaryRow label={t("conciliacion.depTransito", { count: nDep })} value={depTransito}
                  valueColor={depTransito > 0 ? "#4ade80" : undefined}
                  title={t("conciliacion.depTransitoTitle")}/>
                <SummaryRow label={t("conciliacion.retTransito", { count: nRet })} value={retTransito}
                  valueColor={retTransito > 0 ? "#f87171" : undefined}
                  title={t("conciliacion.retTransitoTitle")}/>
                <SummaryRow label={t("conciliacion.saldoLibros")} value={saldoLibros} bold
                  title={t("conciliacion.saldoLibrosTitle")}/>
              </div>

              {!cerrada ? (
                <>
                  <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
                    <button onClick={() => guardarBorrador(false)} disabled={saving || closing}
                      className={`${styles.btn} ${styles["btn-outline"]}`} style={{ flex: 1 }}>
                      <i className={`fa-solid ${saving ? "fa-spinner fa-spin" : "fa-floppy-disk"}`}/>
                      {t("conciliacion.guardarBorrador")}
                    </button>
                    <button onClick={cerrarConciliacion} disabled={!cuadra || saving || closing}
                      className={`${styles.btn} ${styles["btn-primary"]}`} style={{ flex: 1 }}
                      title={cuadra ? t("conciliacion.cerrarTitleOk") : t("conciliacion.cerrarTitleNo")}>
                      <i className={`fa-solid ${closing ? "fa-spinner fa-spin" : "fa-lock"}`}/>
                      {t("conciliacion.cerrarConciliacion")}
                    </button>
                  </div>
                </>
              ) : (
                <p style={{ marginTop: 14, fontSize: 13, color: "#4ade80", fontWeight: 600 }}>
                  <i className="fa-solid fa-lock" style={{ marginRight: 6 }}/>
                  {data.cerrada?.cerrada_at
                    ? t("conciliacion.cerradaEl", { fecha: fmtDate(data.cerrada.cerrada_at.slice(0, 10)) })
                    : t("conciliacion.cerradaSinFecha")}
                </p>
              )}

              <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                <button onClick={() => exportar("pdf")} disabled={!!exporting}
                  className={`${styles.btn} ${styles["btn-pdf"]}`}>
                  <i className={`fa-solid ${exporting === "pdf" ? "fa-spinner fa-spin" : "fa-file-pdf"}`}/>
                  PDF
                </button>
                <button onClick={() => exportar("excel")} disabled={!!exporting}
                  className={`${styles.btn} ${styles["btn-excel"]}`}>
                  <i className={`fa-solid ${exporting === "excel" ? "fa-spinner fa-spin" : "fa-file-excel"}`}/>
                  Excel
                </button>
              </div>

              <p className={styles.hint}>
                {t("conciliacion.hint", { simbolo })}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Modal confirmar eliminación de conciliación */}
      {confirmDel && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)",
          backdropFilter: "blur(3px)", zIndex: 1000, display: "flex",
          alignItems: "center", justifyContent: "center" }}
          onClick={() => !deleting && setConfirmDel(null)}>
          <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-card)",
            borderRadius: 12, padding: 24, width: "min(440px,92vw)",
            boxShadow: "0 24px 64px rgba(0,0,0,0.55)" }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
              <div style={{ background: "rgba(248,113,113,0.15)", borderRadius: 8,
                padding: "8px 11px", color: "#f87171" }}>
                <i className="fa-solid fa-trash" style={{ fontSize: "1.1rem" }}/>
              </div>
              <div>
                <h3 style={{ margin: 0, color: "var(--text-primary)", fontSize: "1rem", fontWeight: 700 }}>
                  {t("conciliacion.modalEliminarTitulo")}
                </h3>
                <p style={{ margin: "2px 0 0", color: "var(--text-muted)", fontSize: "0.78rem" }}>
                  {confirmDel.estado === "CERRADA" ? t("conciliacion.modalEstadoCerrada") : t("conciliacion.modalEstadoBorrador")} · {fmtDate(confirmDel.fecha_corte)} — {simbolo} {formatCurrency(confirmDel.saldo_banco)}
                </p>
              </div>
            </div>
            <div style={{ borderTop: "1px solid var(--border-card)", margin: "0 0 14px" }}/>
            <p style={{ color: "var(--text-secondary)", fontSize: "0.85rem", lineHeight: 1.6, margin: "0 0 18px" }}>
              {confirmDel.estado === "CERRADA"
                ? t("conciliacion.modalBodyCerrada")
                : t("conciliacion.modalBodyBorrador")}
              <strong>{t("conciliacion.accionIrreversible")}</strong>
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => setConfirmDel(null)} disabled={deleting}
                className={`${styles.btn} ${styles["btn-outline"]}`}>
                {t("common.cancelar")}
              </button>
              <button onClick={eliminarConciliacion} disabled={deleting}
                className={`${styles.btn} ${styles["btn-pdf"]}`} style={{ flex: "0 0 auto" }}>
                <i className={`fa-solid ${deleting ? "fa-spinner fa-spin" : "fa-trash"}`}/>
                {deleting ? t("conciliacion.eliminando") : t("conciliacion.siEliminar")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
